/* eslint-disable no-param-reassign */
import loadImage from 'blueimp-load-image';
import { IMAGE_UPLOAD_LINK, CONFIRM_IMAGE_UPLOAD } from './graphql/upload';
import graphQLClient from './graphQLClient';
import stripImageMetadata from './stripImageMetadata';

// SHA-256 (hex) of the exact bytes we PUT, used for per-user dedup. Requires a
// secure context (https / localhost), which all upload entry points are.
const sha256Hex = async blob => {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// Broadcast an upload failure so <ImageQuotaNotifier> can show a snackbar
// (storage-limit hits get an Upgrade CTA). Callers still get a rejected promise.
const notifyUploadError = message => {
  if (typeof window === 'undefined') return;
  const quota = /storage limit/i.test(message || '');
  window.dispatchEvent(
    new CustomEvent('tf:image-upload-error', { detail: { message, quota } }),
  );
};

// prevent ssr problems due to missing window on server
let dataURLtoBlob;
const isWindow = typeof window !== 'undefined';
// eslint-disable-next-line global-require
if (isWindow) dataURLtoBlob = require('blueimp-canvas-to-blob');

const blobToFile = (theBlob, fileName) => {
  // https://stackoverflow.com/questions/27159179/how-to-convert-blob-to-file-in-javascript
  // A Blob() is almost a File() - it's just missing the two properties below which we will add
  theBlob.lastModifiedDate = new Date();
  theBlob.name = fileName;
  return theBlob;
};

// Every upload goes through here, so this is the one place that has to strip
// location metadata. The canvas-resize path drops it as a side effect of
// re-encoding, but three branches upload the ORIGINAL bytes — small files,
// GIFs, and allowFullSize — and those were shipping the author's GPS
// coordinates to a public URL.
//
// The AI Writer still needs the GPS and capture time for its photo timeline,
// so the EXIF is read from the original BEFORE the strip and travels to the
// backend inside confirmImageUpload — the private Media record, never the
// public bytes. Stripping and keeping are not in conflict: the strip protects
// the public URL, the confirm payload feeds the feature.

// Mirrors the backend's own extraction (tf-backend src/ai/exif.js): the same
// exifr options, the same tags, and the server re-validates everything through
// sanitizeExif — a photo with garbage EXIF degrades to "no EXIF", never to a
// failed upload.
const readExif = async file => {
  try {
    const exifr = (await import('exifr')).default;
    const parsed = await exifr.parse(file, {
      tiff: true,
      exif: true,
      gps: true,
    });
    if (!parsed) return undefined;
    const takenAt = parsed.DateTimeOriginal || parsed.CreateDate;
    const camera = [parsed.Make, parsed.Model]
      .filter(part => typeof part === 'string' && part.trim())
      .join(' ')
      .trim();
    const exif = {
      ...(takenAt instanceof Date && !Number.isNaN(takenAt.getTime())
        ? { takenAt: takenAt.toISOString() }
        : {}),
      ...(typeof parsed.latitude === 'number' && typeof parsed.longitude === 'number'
        ? { gpsLat: parsed.latitude, gpsLng: parsed.longitude }
        : {}),
      ...(camera ? { camera } : {}),
    };
    return Object.keys(exif).length ? exif : undefined;
  } catch (error) {
    return undefined;
  }
};

// Bound a fetch that has no timeout of its own. graphql-request 3.x rides the
// plain fetch, and a mobile radio link that drops mid-request can hold that
// promise open for minutes — every other stage of the pipeline (materialise,
// EXIF, PUT) is already bounded, so these two calls were the last way for the
// upload spinner to outlive the author's patience with no error and no end.
const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The connection timed out')),
      ms,
    );
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

const GRAPHQL_TIMEOUT_MS = 30000;

const upload = (original, token, exif) =>
  new Promise((resolve, reject) => {
    // Fail closed: surface every failure (quota/gating/network/PUT) and reject —
    // never resolve as a successful upload with no URL.
    const fail = message => {
      notifyUploadError(message);
      reject(new Error(message));
    };

    if (original.type.split('/')[0] !== 'image')
      return fail('Only image files can be uploaded');

    // Strip BEFORE the sha256: the hash is the dedup key, and hashing the
    // original would let a cleaned re-upload of the same photo miss the cache
    // (and, worse, would key the archive off bytes we never actually store).
    return stripImageMetadata(original)
      .then(file =>
        sha256Hex(file)
          .catch(() => undefined)
          .then(sha256 => {
            const variables = {
              filename: file.name,
              size: file.size,
              sha256,
              token,
            };

            withTimeout(
              graphQLClient(IMAGE_UPLOAD_LINK, variables),
              GRAPHQL_TIMEOUT_MS,
            )
              .then(data => {
                const link = data && data.imageUploadLink;
                if (!link || !link.success)
                  return fail(
                    (link && link.message) || 'Image could not be uploaded',
                  );

                const { fileName, uploadUrl, deduped } = link;
                const publicUrl = `https://img.travelfeed.io/${encodeURIComponent(
                  fileName,
                )}`;

                // Identical bytes already stored for this user — skip the PUT.
                // The EXIF still has to land: the stored Media row may predate
                // client-side extraction, and a duplicate confirm updates its
                // EXIF without re-charging the storage quota (the backend
                // treats the duplicate-key path as exactly this case).
                // AWAITED before resolving: the AI Writer reads the Media row
                // the moment the photo shows as ready, so resolving first was
                // a race that lost the GPS/date for exactly the fast authors.
                // A confirm failure still resolves — the bytes are stored and
                // the editor use case does not need the row.
                if (deduped || !uploadUrl) {
                  if (exif)
                    return withTimeout(
                      graphQLClient(CONFIRM_IMAGE_UPLOAD, {
                        key: fileName,
                        sha256,
                        size: file.size,
                        exif,
                        token,
                      }),
                      GRAPHQL_TIMEOUT_MS,
                    )
                      .catch(() => {})
                      .then(() => resolve(publicUrl));
                  return resolve(publicUrl);
                }

                const xhr = new XMLHttpRequest();
                xhr.open('PUT', uploadUrl, true);
                xhr.setRequestHeader('Content-Type', file.type);
                // The signed URL expires after 120s, so a PUT still unfinished
                // by then can only fail — time it out instead of letting a
                // stalled connection hold the upload spinner forever.
                xhr.timeout = 120000;
                xhr.ontimeout = () => fail('Image could not be uploaded');
                xhr.onerror = () => fail('Image could not be uploaded');
                xhr.onreadystatechange = () => {
                  if (xhr.readyState !== 4) return;
                  if (xhr.status === 200) {
                    // Record the upload for dedup + quota — and the EXIF the
                    // AI Writer needs. AWAITED before resolving (see the
                    // deduped branch above for why); a confirm failure still
                    // resolves rather than failing a stored upload.
                    withTimeout(
                      graphQLClient(CONFIRM_IMAGE_UPLOAD, {
                        key: fileName,
                        sha256,
                        size: file.size,
                        ...(exif ? { exif } : {}),
                        token,
                      }),
                      GRAPHQL_TIMEOUT_MS,
                    )
                      .catch(() => {})
                      .then(() => resolve(publicUrl));
                  } else {
                    fail('Image could not be uploaded');
                  }
                };
                xhr.send(file); // `file` is a File object here
              })
              .catch(err =>
                fail((err && err.message) || 'Image upload failed'),
              );
          }),
      )
      .catch(err => fail((err && err.message) || 'Image upload failed'));
  });

// Read the picked file's bytes ONCE, up front, into a plain in-memory File.
//
// A File from iOS's photo picker is a handle, not bytes: WebKit materialises
// it lazily from the photo library (HEIC transcode, iCloud download). When
// that provider fails after the change event has fired — which single-select
// is prone to, its transcode races the page — every later read of the handle
// neither succeeds nor errors: exifr stalls, blueimp's hidden <img> never
// fires load OR error, and the upload spinner runs forever with nothing to
// time it out. Reading the bytes here converts that into the two outcomes the
// rest of the pipeline can handle: real bytes (downstream now operates on
// memory and cannot stall), or a visible failure within the timeout.
const MATERIALIZE_TIMEOUT_MS = 30000;

const materialize = file =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Reading the photo timed out')),
      MATERIALIZE_TIMEOUT_MS,
    );
    file
      .arrayBuffer()
      .then(buffer => {
        clearTimeout(timer);
        if (!buffer || !buffer.byteLength)
          reject(new Error('The photo could not be read'));
        else
          resolve(
            new File([buffer], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            }),
          );
      })
      .catch(error => {
        clearTimeout(timer);
        reject(error);
      });
  });

const uploadFile = async (original, options) => {
  let localfile;
  try {
    localfile = await materialize(original);
  } catch (error) {
    notifyUploadError(
      'That photo could not be read from your library. Please pick it again.',
    );
    throw new Error('Image could not be read');
  }
  // iPhones shoot HEIC. Most picker paths transcode to JPEG on the way out,
  // but the Files app — and some picker configurations — hand over the
  // original. A raw HEIC must never be uploaded: stripImageMetadata only
  // rewrites JPEG/PNG, so its GPS would ship to a public URL, and no
  // non-Apple browser can decode the raw file. Any browser that can DECODE
  // it (Safari can, natively) re-encodes it to JPEG below; one that cannot
  // fails cleanly in the loadImage guard.
  const isHeic =
    /^image\/hei[cf]$/i.test(localfile.type) ||
    /\.hei[cf]$/i.test(localfile.name || '');

  // Read from the ORIGINAL file, before any path is chosen: the canvas path
  // re-encodes (EXIF gone from the blob) and the direct path strips — either
  // way the only reliable source is the file the author handed us. Raced
  // against a timeout: EXIF is an enrichment, and no parser stall may hold
  // the upload itself.
  const exif = await Promise.race([
    readExif(localfile),
    new Promise(resolve => {
      setTimeout(() => resolve(undefined), 5000);
    }),
  ]);
  return new Promise((resolve, reject) => {
    const { token } = options;
    // Resize large images before uploading. The image and the canvas are used
    // through their own references and never enter the page: they used to be
    // looked up by an id made from the file name, and the direct upload below
    // left its image in the page, so a later photo with the same name got the
    // earlier photo's size, or on the resize path its pixels.
    loadImage(
      localfile,
      async img => {
        // blueimp hands the callback an ERROR EVENT rather than an image when
        // the file cannot be decoded — a truncated download, a .png that is not
        // one, a photo in a format this browser will not read. Treating that as
        // an image throws inside blueimp's own error handler, where nothing
        // catches it, so this promise never settled and the caller waited for
        // an upload that was never going to finish.
        if (!img || !(img instanceof HTMLElement)) {
          notifyUploadError('That file could not be read as an image');
          reject(new Error('Image could not be read'));
          return undefined;
        }
        // blueimp scales by the width/height attributes; natural* stay the
        // file's own size.
        const { height, width, naturalWidth, naturalHeight } = img;
        // don't resize images smaller than the allowed size — but a HEIC is
        // never uploaded as-is, whatever its size (see isHeic above)
        if (
          !isHeic &&
          (naturalWidth < 2561 ||
            localfile.size < 1000000 ||
            localfile.type === 'image/gif' ||
            options.allowFullSize)
        ) {
          return upload(localfile, token, exif)
            .then(result => {
              resolve({
                url: result,
                width: naturalWidth,
                height: naturalHeight,
              });
            })
            .catch(() => reject(new Error('Image could not be uploaded')));
        }
        // A HEIC re-encodes to JPEG: toDataURL('image/heic') is an
        // unsupported encode that silently falls back to PNG — bigger than
        // the original, which the size comparison below would then "fix" by
        // uploading the raw HEIC. allowFullSize (the AI Writer) keeps the
        // full resolution through the conversion.
        const targetType = isHeic ? 'image/jpeg' : localfile.type;
        const targetName = isHeic
          ? `${(localfile.name || 'photo').replace(/\.hei[cf]$/i, '')}.jpg`
          : localfile.name;
        const fullRes = isHeic && options.allowFullSize;
        const canvas = document.createElement('canvas');
        canvas.width = fullRes ? naturalWidth : width;
        canvas.height = fullRes ? naturalHeight : height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const durl = canvas.toDataURL(targetType); // This will save your image as a
        const blob = await dataURLtoBlob(durl);
        // jpeg file in the base64 format.
        let resfile = await blobToFile(blob, targetName);
        // Upload original file instead if the resizing saves less than 25% —
        // never for HEIC, whose original must not reach the archive
        if (!isHeic && resfile.size > localfile.size * 0.75) resfile = localfile;

        return upload(resfile, token, exif)
          .then(result => {
            resolve({
              url: result,
              width: naturalWidth,
              height: naturalHeight,
            });
          })
          .catch(() => reject(new Error('Image could not be uploaded')));
      },
      { maxWidth: 1920 },
    );
  });
};

export default uploadFile;
